import * as React from "react";
import {Grid2 as Grid} from "@mui/material";
import {useTranslation} from "react-i18next";
import {RequestI18N} from "@/translations";
import DateInputField from "@/lib/components/inputfields/date-input-field";
import {ExtendDatesMinDates} from "@/pages/internal-request-page/schema";
import {NamedValue} from "@/lib/modelstate";
import {FieldError} from "@/lib/utils";
import dayjs from "dayjs";

/** A date value that can be a string, Date object, or null/undefined. */
type DateValue = string | Date | null;

interface RequestDateFieldsProps {
    /** Field name for the validUntil date input. */
    nameValidUntil: string;
    /** Field name for the deliveryDateFrom date input. */
    nameDeliveryDateFrom: string;
    /** Field name for the deliveryDateTo date input. */
    nameDeliveryDateTo: string;
    /** Current value for the validUntil field. */
    validUntil?: DateValue;
    /** Current value for the deliveryDateFrom field. */
    deliveryDateFrom?: DateValue;
    /** Current value for the deliveryDateTo field. */
    deliveryDateTo?: DateValue;
    /** Validation errors for each field. */
    errors?: {
        validUntil?: FieldError;
        deliveryDateFrom?: FieldError;
        deliveryDateTo?: FieldError;
    };
    /** onChange handler from useModelState. */
    onChange?: (field: NamedValue) => void;
    /** Whether all fields are disabled. */
    disabled?: boolean;
    /**
     * Optional minimum dates (for the extend-dates dialog).
     * When provided, the user cannot select a date earlier than the current value.
     */
    minDates?: ExtendDatesMinDates;
}

/**
 * Renders the three date fields: validUntil, deliveryDateFrom, deliveryDateTo.
 * Used in both RequestCard (with "orderData." prefix for delivery dates) and
 * ExtendDatesDialog (flat field names).
 * Supports optional minimum date constraints for the extend-dates use case.
 *
 * @param props - See RequestDateFieldsProps.
 */
const RequestDateFields: React.FC<RequestDateFieldsProps> = ({
    nameValidUntil,
    nameDeliveryDateFrom,
    nameDeliveryDateTo,
    validUntil,
    deliveryDateFrom,
    deliveryDateTo,
    errors,
    onChange,
    disabled,
    minDates
}) => {
    const { t } = useTranslation();

    return (
        <>
            <Grid size={{ sm: 6 }}>
                <DateInputField
                    name={nameValidUntil}
                    label={t(RequestI18N.details.field.requestValidity)}
                    value={validUntil}
                    error={errors?.validUntil}
                    onChange={onChange}
                    disabled={disabled}
                    minDate={minDates?.validUntil ? dayjs(minDates.validUntil) : undefined}
                />
            </Grid>
            <Grid size={{ sm: 6 }}>
                <DateInputField
                    name={nameDeliveryDateFrom}
                    label={t(RequestI18N.details.field.deliveryDateFrom)}
                    value={deliveryDateFrom}
                    error={errors?.deliveryDateFrom}
                    onChange={onChange}
                    disabled={disabled}
                    minDate={minDates?.deliveryDateFrom ? dayjs(minDates.deliveryDateFrom) : undefined}
                />
            </Grid>
            <Grid size={{ sm: 6 }}>
                <DateInputField
                    name={nameDeliveryDateTo}
                    label={t(RequestI18N.details.field.deliveryDateTo)}
                    value={deliveryDateTo}
                    error={errors?.deliveryDateTo}
                    onChange={onChange}
                    disabled={disabled}
                    minDate={minDates?.deliveryDateTo ? dayjs(minDates.deliveryDateTo) : undefined}
                />
            </Grid>
        </>
    );
};

export default RequestDateFields;
